import React, { useMemo, useState } from 'react';
import { Controller, ControllerProps } from 'react-hook-form';
import { useQuery } from 'react-query';
import { SearchOrgFilter, SearchOrgResultExternal } from '@askporter/client-grieg-lyric';
import { BaseAutocomplete } from '@askporter/component-library';
import type { BaseAutocompleteValue } from '@askporter/component-library';
import { SelectItem, generateDropDownList, useDebounce } from '@askporter/utils';
import { API } from '../../api';

interface ControlledOrgAutocompleteProps extends Omit<ControllerProps, 'render'> {
  /**
   * Translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * Field label
   */
  label: string;
  /**
   * Current error state, should be true if there is an error
   */
  error: boolean;
  /**
   * The error string to display
   */
  errorMessage: string;
  /**
   * Is a small device or not (kept for backwards compatibility, but BaseAutocomplete handles responsive behavior internally)
   */
  isSmallDevice?: boolean;
  /**
   * Optional filter property to be passed into the orgs search query
   */
  orgSearchfilter?: SearchOrgFilter;
  /**
   * Placeholder text for the input
   */
  placeholder?: string;
}

/**
 * Extends the Autocomplete to create ControlledOrgAutocomplete
 * @param t - translation function
 * @param label - field label
 * @param error - current error state, should be true if there is an error
 * @param errorMessage -  the error string to display
 * @param isSmallDevice - is a small device or not
 * @param orgSearchFilter - optional filter property to be passed into the orgs search query
 * @param placeholder - placeholder text for the input
 */
const ControlledOrgAutocomplete: React.FC<React.PropsWithChildren<ControlledOrgAutocompleteProps>> = ({
  t,
  label,
  error,
  errorMessage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSmallDevice: _isSmallDevice, // kept for backwards compatibility but BaseAutocomplete handles responsive behavior internally
  orgSearchfilter: filter,
  placeholder,
  ...controllerProps
}: ControlledOrgAutocompleteProps) => {
  const [orgSearchValue, setOrgSearchValue] = useState('');
  const debouncedOrg = useDebounce<string>(orgSearchValue, 700);
  const postOrgSearchKey = {
    path: 'orgs/search',
    payload: {
      freeText: debouncedOrg,
      page: 1,
      // otherwise it could be set to 'undefined' and reported as en error in 'setupGriegIntercepts'
      ...(filter ? { filter } : {}),
    },
  };
  const { data: { data: orgSearch = {} } = {}, isLoading } = useQuery<{
    data: SearchOrgResultExternal;
  }>([postOrgSearchKey], () => API().post({ ...postOrgSearchKey }), {
    enabled: debouncedOrg === '' || debouncedOrg?.length > 2,
  });

  const orgAutocompleteOptions = generateDropDownList(orgSearch?.results || [], 'urn', 'name');

  // Convert SelectItem[] to BaseAutocompleteValue[]
  const baseOptions: BaseAutocompleteValue[] = useMemo(
    () =>
      orgAutocompleteOptions
        .filter((option) => !option.hidden)
        .map((option) => ({
          uid: String(option.value),
          label: option.label || String(option.value),
          iconPath: option.iconPath,
        })),
    [orgAutocompleteOptions],
  );

  // Convert SelectItem to BaseAutocompleteValue
  const selectItemToBaseValue = (
    item: SelectItem<string | number | readonly string[]> | null,
  ): BaseAutocompleteValue | null => {
    if (!item) return null;
    return {
      uid: String(item.value),
      label: item.label || String(item.value),
      iconPath: item.iconPath,
    };
  };

  // Convert BaseAutocompleteValue back to SelectItem
  const baseValueToSelectItem = (
    baseValue: BaseAutocompleteValue | null,
  ): SelectItem<string | number | readonly string[]> | null => {
    if (!baseValue) return null;
    // Find the original option to preserve the full SelectItem structure
    const originalOption = orgAutocompleteOptions.find((opt) => String(opt.value) === baseValue.uid);
    return (
      originalOption || {
        value: baseValue.uid,
        label: baseValue.label,
        iconPath: baseValue.iconPath,
      }
    );
  };

  return (
    <>
      <Controller
        defaultValue={null}
        {...controllerProps}
        render={({ field }) => {
          // Convert field value (SelectItem) to BaseAutocompleteValue
          const baseValue = selectItemToBaseValue(field.value);

          return (
            <BaseAutocomplete
              ref={field.ref}
              t={t}
              idPrefix={controllerProps.name}
              error={error}
              helperText={error ? errorMessage : undefined}
              label={label}
              placeholder={placeholder || t('ns.common:type_to_search')}
              options={baseOptions}
              value={baseValue}
              onChange={(newBaseValue: BaseAutocompleteValue | null) => {
                // Convert BaseAutocompleteValue back to SelectItem for react-hook-form
                const newSelectItem = baseValueToSelectItem(newBaseValue);
                // Type assertion: org URNs are strings, not arrays
                field.onChange(newSelectItem as SelectItem<string | number> | null);
              }}
              onInputChange={(_: React.SyntheticEvent | null, newInputValue: string) => {
                setOrgSearchValue(newInputValue);
              }}
              inputValue={orgSearchValue}
              loading={isLoading}
              freeSolo
              filterOptions={(o) => o}
              mapOptionToValue={(option: BaseAutocompleteValue) => ({
                ...option,
                label: option.label || option.uid,
              })}
              showLabel={!!label}
              clearable={false}
            />
          );
        }}
      />
    </>
  );
};

export default ControlledOrgAutocomplete;
